// ---------------------------------------------------------------------------
// PaymentMethodList -- Marketing-context payment method list rendered from the
// catalog. Shows each method's brand logo(s) (or default icon) plus its
// localized label, in a horizontal flex layout.
//
// Used on the public Preise page; pairs with PaymentMethodStep in the wizard
// (which shares the same PaymentMethodVisual under the hood).
// ---------------------------------------------------------------------------

import { useLocale } from "next-intl";

import type { CatalogPaymentMethod } from "@/lib/api/catalog";
import { cn } from "@/lib/utils";

import PaymentMethodVisual from "./PaymentMethodVisual";

export interface PaymentMethodListProps {
  methods: CatalogPaymentMethod[];
  className?: string;
}

export default function PaymentMethodList({
  methods,
  className,
}: PaymentMethodListProps) {
  const locale = useLocale();
  const lang: "de" | "en" = locale === "en" ? "en" : "de";

  if (methods.length === 0) {
    return null;
  }

  return (
    <ul
      className={cn(
        "grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3",
        className,
      )}
    >
      {methods.map((method) => (
        <li
          key={method.slug}
          className="border-border bg-card flex min-h-32 flex-col items-center justify-center gap-3 rounded-2xl border px-5 py-6 text-center shadow-sm"
        >
          <PaymentMethodVisual
            method={{
              slug: method.slug,
              logos:
                method.logos.length > 0 || method.slug !== "pay_over_invoice"
                  ? method.logos
                  : ["prepay"],
            }}
            size="md"
          />
          <span className="text-muted-foreground text-base leading-tight font-medium">
            {method.label[lang]}
          </span>
        </li>
      ))}
    </ul>
  );
}
